import ApiClient from "../apiClient";

export type StrategySourceState =
  | "live"
  | "mock"
  | "needs_credentials"
  | "rate_limited"
  | "malformed"
  | "error"
  | "no_data";

export interface PerformanceStrategyInput {
  instrument: string;
  session: "ny" | "london" | "asia";
  setup: "opening-range-breakout" | "initial-balance" | "gap-fill";
  timeframe: "5min" | "15min" | "30min" | "1hour";
  riskPosture: "conservative" | "balanced" | "aggressive";
  startDate?: string;
  endDate?: string;
}

export interface StrategyStat {
  label: string;
  value: string;
  detail: string;
}

export interface StrategySessionWindow {
  startTime: string;
  endTime: string;
  timezone: string;
  label: string;
}

export interface PerformanceStrategyDraft {
  input: Required<PerformanceStrategyInput>;
  generatedAt: string;
  edgeful: {
    sourceState: StrategySourceState;
    source: string;
    reportName: string;
    reportPath: string;
    ticker: string;
    sessionWindow: StrategySessionWindow;
    stats: StrategyStat[];
    error?: string;
  };
  performance: {
    projectxStatus: string;
    tradeCount: number;
    netPnl: number;
    winRate: number | null;
    kpis: StrategyStat[];
  };
  thesis: string;
  statsUsed: StrategyStat[];
  confluence: string[];
  antiConfluence: string[];
  invalidation: string;
  riskNotes: string[];
  sources: string[];
  advisoryBoundary: string;
}

export interface PerformanceStrategyContext {
  defaults: Required<PerformanceStrategyInput>;
  options: {
    instruments: string[];
    sessions: PerformanceStrategyInput["session"][];
    setups: PerformanceStrategyInput["setup"][];
    timeframes: PerformanceStrategyInput["timeframe"][];
    riskPostures: PerformanceStrategyInput["riskPosture"][];
  };
  edgeful: { configured: boolean; baseUrl: string };
  projectxStatus: string;
}

export interface PerformanceStrategyService {
  getStrategyContext: () => Promise<PerformanceStrategyContext>;
  createStrategyDraft: (
    input: PerformanceStrategyInput,
  ) => Promise<PerformanceStrategyDraft>;
  reviewStrategyDraft: (input: {
    draftText: string;
    question?: string;
  }) => Promise<{ generatedAt: string; review: string }>;
}

export function createPerformanceStrategyService(
  client: ApiClient,
): PerformanceStrategyService {
  return {
    getStrategyContext: () =>
      client.get<PerformanceStrategyContext>(
        "/api/performance/strategy-context",
      ),
    createStrategyDraft: (input) =>
      client.post<PerformanceStrategyDraft>(
        "/api/performance/strategy-draft",
        input,
      ),
    reviewStrategyDraft: (input) =>
      client.post("/api/performance/strategy-review", input),
  };
}
